<template>
  <AdminTopBar :title="product.title || 'Nieuw product'">
    <template #actions>
      <Button responsive icon="ArrowLeft" @click="router.push({name: 'admin-products'})">
        Terug
      </Button>
      <Button responsive icon="Trash" type="button" @click="remove(route.params.id)">
        Verwijder
      </Button>
      <Button responsive icon="Printer" type="button" @click.prevent="printLabel({type:'qr', data: { line1: product.title, line2: product.description || '', url: `https://hengeloserevue.nl/?p=${route.params.id}`}})">
        Print label
      </Button>
      <Button
        responsive
        icon="Save"
        :disabled="loading"
        type="submit"
        :color="loading ? '': 'cta'"
        @click.prevent="submit">
        Opslaan
      </Button>
    </template>
  </AdminTopBar>

  <AdminForm @submit.stop="submit">
    <fieldset :class="$style['fieldset']">
      <legend>Afbeeldingen</legend>

      <Card v-if="imagePreview && !id">
        <template #image>
          <img :src="imagePreview">
        </template>
      </Card>
      <Card v-for="image in product.images" :key="image.fileId">
        <template #image>
          <img :src="`${config.public.apiUrl}/storage/products/thumbs/${image.fileId}.webp`">
        </template>
        <template #actions>
          <Button with-border icon="Trash2" type="button" @click="deleteImage(image.id)">
            Verwijder
          </Button>
        </template>
      </Card>

      <FormInput
        v-if="!id && !imagePreview"
        ref="fileInput"
        label="Foto toevoegen"
        type="file"
        name="upload"
        capture
        @input.stop="addImage($event)" />

      <Button v-if="!id && imagePreview" @click="imagePreview = null">
        Verwijder foto
      </Button>

      <small v-if="!id && imagePreview">Als je meerdere foto's wilt toevoegen, moet je eerst het product opslaan</small>
    </fieldset>
    <fieldset :class="$style['fieldset']">
      <legend>Details</legend>
      <FormInput v-model="product.title" name="title" label="Titel" />
      <FormInput v-model="product.slug" name="slug" label="Slug" />
      <FormInput v-model="product.quantity" name="quantity" label="Aantal" type="number" />
      <FormTextarea v-model="product.description" rows="5" name="description" label="Omschrijving" />
      <FormToggle
        v-model="product.visible"
        name="visibility"
        :true-value="1"
        :false-value="0"
        label="Zichtbaarheid"
        type="checkbox" />
    </fieldset>

    <fieldset :class="$style['fieldset']">
      <legend>Categoriën</legend>
      <Button
        v-for="category in availableCategories"
        :key="category.id"
        type="button"
        icon-pos="right"
        :with-border="!!isRelationActive('categories', category)"
        :icon="!!isRelationActive('categories', category)? 'Check' : undefined"
        @click="setRelation('categories', category) ">
        {{ category.title }}
      </Button>
      <Button icon="Plus" @click.prevent="showModal = 'category'">
        Categorie toevoegen
      </Button>
      <Modal v-if="showModal ==='category'" @close="showModal = null">
        <AdminCategoryForm @close="showModal = null; refreshCategories()" />
      </Modal>
    </fieldset>

    <fieldset :class="$style['fieldset']">
      <legend>Thema</legend>
      <Button
        v-for="theme in availableThemes"
        :key="theme.id"
        type="button"
        icon-pos="right"
        :with-border="!!isRelationActive('themes', theme)"
        :icon="!!isRelationActive('themes', theme)? 'Check' : undefined"
        @click="setRelation('themes', theme) ">
        {{ theme.title }}
      </Button>
      <Button icon="Plus" @click.prevent="showModal = 'theme'">
        Thema toevoegen
      </Button>
      <Modal v-if="showModal ==='theme'" @close="showModal = null">
        <AdminThemeForm @close="showModal = null; refreshThemes()" />
      </Modal>
    </fieldset>

    <fieldset :class="$style['fieldset']">
      <legend>Kleuren</legend>
      <Button
        v-for="color in availableColors"
        :key="color.id"
        type="button"
        icon-pos="right"
        :with-border="!!isRelationActive('colors', color)"
        :icon="!!isRelationActive('colors', color)? 'Check' : undefined"
        @click="setRelation('colors', color) ">
        {{ color.title }}
        <ColorPreview :color="color" />
      </Button>
      <Button icon="Plus" @click.prevent="showModal = 'color'">
        Kleur toevoegen
      </Button>
      <Modal v-if="showModal ==='color'" @close="showModal = null">
        <AdminColorForm @close="showModal = null; refreshColors()" />
      </Modal>
    </fieldset>

    <fieldset :class="$style['fieldset']">
      <legend>Locaties</legend>
      <Button
        v-for="location in availableLocations"
        :key="location.id"
        type="button"
        icon-pos="right"
        :with-border="!!isRelationActive('locations', location)"
        :icon="!!isRelationActive('locations', location)? 'Check' : undefined"
        @click="setRelation('locations', location) ">
        {{ location.title }}
      </Button>
      <Button icon="Plus" @click.prevent="showModal = 'location'">
        Locatie toevoegen
      </Button>
      <Modal v-if="showModal ==='location'" @close="showModal = null">
        <AdminLocationForm @close="showModal = null; refreshLocations()" />
      </Modal>
    </fieldset>

    <fieldset :class="$style['fieldset']">
      <legend>Koppel producten</legend>
      <AdminProductRelated :id="product.id" :related="product.relatedProducts" />
    </fieldset>

    <!-- <fieldset :class="$style['fieldset']">
      <legend>Koppel producten</legend>
      <AdminProductRelated :id="product.id" />
    </fieldset> -->

    <fieldset :class="$style['fieldset']">
      <legend>Metadata</legend>
      <div><b>Aangemaakt</b> {{ product.createdAt }}</div>
      <div><b>Laast aangepast</b></div>
      {{ product.updatedAt }}
    </fieldset>
  </AdminForm>
</template>
<script setup lang="ts">
import { useForm } from 'vee-validate'
import { slugify } from '~/helpers/slugify'
const { printLabel } = useDymo()
const props = defineProps<{
  id?: number | string | null
}>()
const { $api } = useNuxtApp()
const config = useRuntimeConfig()
const router = useRouter()
const route = useRoute()
const fileInput = ref()
const imagePreview = ref()
const showModal = ref()
const loading = ref(false)
let fileToUpload: any = null
const productUrl = props.id ? ref(`products/${props.id}`) : ref('products')
const { data: availableThemes, refresh: refreshThemes } = await useApi('themes')
const { data: availableLocations, refresh: refreshLocations } = await useApi('locations')
const { data: availableColors, refresh: refreshColors } = await useApi('colors')
const { data: availableCategories, refresh: refreshCategories } = await useApi('categories')
const { data: product, refresh } = await useAsyncData('product-' + props?.id, () => {
  if (!props.id) {
    return {
      title: null,
      slug: null,
      description: null,
      visible: 1,
      quantity: 1,
      locations: [],
      themes: [],
      colors: [],
      images: null,
      categories: [],
      createdAt: null,
      updatedAt: null
    }
  }

  return $api(`products/${props.id}`)
})

const submit = async () => {
  loading.value = true
  try {
    const { id } = await $api(productUrl.value, {
      method: props.id ? 'PUT' : 'POST',
      body: product.value
    })

    if (!props.id && fileToUpload) {
      await uploadImage(id)
    }

    if (id) {
      loading.value = false
      await router.push(props.id ? { name: 'admin-products' } : { name: 'admin-products-id', params: { id } })
    }
  } catch (e) {
    e.data.errors?.forEach(error => {
      setFieldError(error.field, error.message)
    })
  }
}

const remove = async (id: any) => {
  if (!await confirm('Product verwijderen?')) { return false }
  await $api(productUrl.value, { method: 'DELETE' })
  await router.push({ name: 'admin-products' })
}

const uploadImage = async id => {
  loading.value = true
  const formData = new FormData()
  formData.append('image', fileToUpload)
  await $api(`products/${id}/images`, {
    method: 'POST',
    body: formData
  })
  fileInput.value = null
  loading.value = false
  return await refresh()
}

const addImage = async event => {
  if (props.id) {
    fileToUpload = event.target.files[0]
    return await uploadImage(props.id)
  }

  const [file] = event.target.files
  if (file) {
    imagePreview.value = URL.createObjectURL(file)
    fileToUpload = file
  }
}

const deleteImage = async (imageId: number) => {
  if (!confirm('Afbeelding verwijderen?')) { return }
  await $api(`${productUrl.value}/images/${imageId}`, { method: 'DELETE' })
  await refresh()
}

const isRelationActive = (relationKey: string, relation: any) => {
  return product.value[relationKey].findIndex(relationRow => relationRow.id === relation.id) > -1
}

const setRelation = (relationKey: string, relation:any) => {
  const index = product.value[relationKey].findIndex(relationRow => relationRow.id === relation.id)

  if (index > -1) {
    return product.value[relationKey].splice(index, 1)
  }

  return product.value[relationKey].push(relation)
}

watch(() => product.value.title, () => {
  product.value.slug = slugify(product.value.title)
})
</script>
<style module>
.fieldset {
  border: 1px solid var(--border-subtle);
  border-radius: var(--border-radius);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xl);
  padding: var(--spacing-xl);
}

legend {
  padding: var(--spacing);
}
</style>
